import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { formatDate } from '../../../lib/dateFormat';
import {
  fetchTeacherOrders,
  getOrderDownloadUrl,
  type TeacherOrderListItem,
  type OrderStatus,
} from '../../../lib/orders';

const STATUS_OPTIONS: Array<{ value: 'all' | OrderStatus; label: string }> = [
  { value: 'all', label: 'Alle Status' },
  { value: 'angelegt', label: 'Angelegt' },
  { value: 'versendet', label: 'Versendet' },
  { value: 'angenommen', label: 'Angenommen' },
  { value: 'abgelehnt', label: 'Abgelehnt' },
  { value: 'bestätigt', label: 'Bestätigt' },
  { value: 'zurückgezogen', label: 'Zurückgezogen' },
  { value: 'beendet', label: 'Beendet' },
];

function orderStatusBadge(status: string) {
  switch (status) {
    case 'angelegt':
      return <Badge variant="outline">Angelegt</Badge>;
    case 'versendet':
      return <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700">Versendet</Badge>;
    case 'angenommen':
      return <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Angenommen</Badge>;
    case 'abgelehnt':
      return <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">Abgelehnt</Badge>;
    case 'bestätigt':
      return <Badge variant="outline" className="border-purple-300 bg-purple-50 text-purple-700">Bestätigt</Badge>;
    case 'zurückgezogen':
      return <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">Zurückgezogen</Badge>;
    case 'beendet':
      return <Badge variant="outline" className="border-slate-300 bg-slate-50 text-slate-600">Beendet</Badge>;
    default:
      return <Badge variant="outline">{status || '—'}</Badge>;
  }
}

function sourceLabel(order: TeacherOrderListItem): string | null {
  if (order.migrationCaseId) return `Migration #${order.migrationCaseId}`;
  if (order.trialLessonId) return `Probestunde #${order.trialLessonId}`;
  if (order.enrollmentId) return `Anmeldung #${order.enrollmentId}`;
  return null;
}

export default function TeacherOrdersSection({ wpUserId }: { wpUserId: number }) {
  const [orders, setOrders] = useState<TeacherOrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (wpUserId <= 0) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const items = await fetchTeacherOrders(
        wpUserId,
        statusFilter === 'all' ? undefined : statusFilter
      );
      setOrders(items);
    } catch (e) {
      setOrders([]);
      setError(e instanceof Error ? e.message : 'Aufträge konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [wpUserId, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const emptyMessage = useMemo(() => {
    if (statusFilter === 'all') return 'Noch keine Aufträge für diesen Lehrer.';
    return `Keine Aufträge mit Status „${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? statusFilter}“.`;
  }, [statusFilter]);

  const handleDownload = async (orderId: number) => {
    setDownloadingId(orderId);
    try {
      const url = await getOrderDownloadUrl(orderId);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'PDF konnte nicht geladen werden.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {loading ? 'Aufträge werden geladen…' : `${orders.length} Auftrag${orders.length === 1 ? '' : 'e'}`}
        </p>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as 'all' | OrderStatus)}
        >
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue placeholder="Status filtern" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Aufträge werden geladen…
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((order) => {
            const source = sourceLabel(order);
            const title = order.fachName || order.unterrichtsformName || 'Unterricht';
            return (
              <div key={order.id} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{order.studentName || 'Schüler'}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {title}
                      {order.unterrichtsformName && order.fachName ? ` · ${order.unterrichtsformName}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Auftrag #{order.id}
                      {source ? ` · ${source}` : ''}
                    </p>
                  </div>
                  {orderStatusBadge(order.status)}
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground uppercase tracking-wide">Geplanter Start</p>
                    <p className="font-medium">{order.plannedStartDate ? formatDate(order.plannedStartDate) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground uppercase tracking-wide">Erstellt</p>
                    <p className="font-medium">{order.createdAt ? formatDate(order.createdAt.slice(0, 10)) : '—'}</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!order.hasContractPdf || downloadingId === order.id}
                    onClick={() => void handleDownload(order.id)}
                    className="gap-1.5"
                  >
                    {downloadingId === order.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Vertrag PDF
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
